"use client";

// WO-GF-001-P1B-S35 · F233 — THE TRANSPORT PROFILE'S "STOPS" BUTTON.
//
// FOUNDER WALK 2026-07-27: "TRANSPORT PROFILE 'STOPS' BUTTON: swaps the media area above from the
// operator's PICTURES to a MAP of ALL the stops that operator actually runs (live's own behaviour).
// Serve it on the KeylessMap/F192 pin stack with real served coordinates — no invented stops."
//
// WHAT WAS THERE. The mirror captured both tabs, neither did anything, and the STOPS one carried the
// mirror's own `disabled` class:
//     <button class="CentersliderText active-tab-button webOnly">…<span>PICS</span></button>
//     <button class="CentersliderText disabled">…<span>STOPS</span></button>
// Live's own markup, click-gated, so the map behind it was never captured (R6-6 "dead interaction").
// This is the S29 dead-control class in its purest form: visibly present, shaped like a tab, inert.
//
// DOCUMENT-SCOPED HYDRATOR, same family as ScheduleDataLayer and ScheduleMapLayer — it wires captured
// mirror markup rather than re-rendering the page, because apps/root serves inlined captured CSSOM per
// page and re-authoring this section would move the mirror baseline on every operator profile.
//
// WHERE THE STOPS COME FROM, and what is NOT invented. `payload.schedules[].routes[].details[]` is the
// operator's own published stop list, captured from live's `__NEXT_DATA__` (schedule-capture.mjs). The
// union across EVERY season and BOTH directions is "all the stops that operator actually runs" — one
// season would under-report an operator like Epic Rides, which publishes eight. Coordinates come from
// `/api/transport/geo`, the same served endpoint the booking-flow StopMap reads, matched by name the
// same way StopMap matches: exact first, then containment either way.
//
// THE HONEST PART: a stop we hold NO served coordinate for gets NO marker. It is counted and published
// on `data-stops-unresolved` rather than dropped silently or guessed onto a plausible-looking point — a
// map with an invented pin is worse than a map with one fewer pin. The gate reads both numbers, so the
// coverage is asserted rather than assumed.
//
// PIXEL NOTE — one deliberate at-rest change. Everything here is inert until the button is clicked,
// EXCEPT that the STOPS button loses the mirror's `disabled` class (opacity .5 → 1). That IS the
// finding: a live control still rendering at half opacity would be the same dead-looking tab with a
// handler hidden behind it. Recorded as a ruling-driven baseline move.

import { useEffect } from "react";
import { createRoot, type Root } from "react-dom/client";
// finding 8 (cold load) — code-split boundary; see KeylessMapLazy.
import KeylessMap from "@/components/map/KeylessMapLazy";
import type { KeylessMarker } from "@/components/map/KeylessMap";
import type { SchedulePayload } from "@/lib/schedule-data";

/** Matches the captured carousel's height so the swap does not reflow the page around it. */
const MEDIA_HEIGHT = 310;

const norm = (s: string) => s.trim().toLowerCase();

export default function StopsMediaLayer({ payload }: { payload: SchedulePayload }) {
  useEffect(() => {
    // The two captured tabs, found by their VISIBLE LABEL rather than by class: `active-tab-button`
    // moves between them, and `disabled` is precisely what this layer removes.
    const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>("button.CentersliderText"));
    const picsBtn = tabs.find((b) => (b.textContent || "").trim().toUpperCase() === "PICS");
    const stopsBtn = tabs.find((b) => (b.textContent || "").trim().toUpperCase() === "STOPS");
    // the media area ABOVE the tab bar — live's own carousel container
    const media = document.querySelector<HTMLElement>('[class*="map-carousel-container"]');
    if (!picsBtn || !stopsBtn || !media) return; // page shape differs → do nothing at all

    // ── the operator's real stop set: every stop, every season, both directions, de-duped by name ────
    const stopNames: string[] = [];
    const seen = new Set<string>();
    for (const season of payload.schedules ?? []) {
      for (const route of season.routes ?? []) {
        for (const stop of route.details ?? []) {
          const n = (stop.name || "").trim();
          if (!n || seen.has(norm(n))) continue;
          seen.add(norm(n));
          stopNames.push(n);
        }
      }
    }
    // Nothing published → leave the dead tab dead. An empty map would be a worse answer than an
    // honestly untouched control.
    if (!stopNames.length) return;

    const host = document.createElement("div");
    host.setAttribute("data-stops-map", "");
    host.setAttribute("data-stops-served", String(stopNames.length));
    host.style.cssText = `display:none;height:${MEDIA_HEIGHT}px;overflow:hidden;border-radius:10px;`;
    media.parentElement?.insertBefore(host, media.nextSibling);
    const root: Root = createRoot(host);

    let rendered = false;
    let alive = true;

    const renderMap = async () => {
      if (rendered) return;
      rendered = true;
      const geo = await fetch("/api/transport/geo")
        .then((r) => r.json())
        .catch(() => null);
      if (!alive) return;
      // FOUNDER MAP RULING 2026-08-27 — the endpoint answers `source:"stub"` when the legacy RDS is
      // unreachable, and stub coordinates may not reach a visitor. Refused rather than pinned; the
      // unresolved count below then reports every stop honestly instead of showing invented places.
      const served: { name?: string; lat?: number; lng?: number }[] =
        geo?.source === "stub" || !Array.isArray(geo?.stops) ? [] : geo.stops;
      const markers: KeylessMarker[] = [];
      const unresolved: string[] = [];
      for (const name of stopNames) {
        const hit =
          served.find((s) => s.name && norm(s.name) === norm(name)) ??
          served.find((s) => s.name && (norm(s.name).includes(norm(name)) || norm(name).includes(norm(s.name))));
        if (hit && Number.isFinite(hit.lat) && Number.isFinite(hit.lng)) {
          markers.push({
            lat: hit.lat as number,
            lng: hit.lng as number,
            name,
            flag: "stop",
            key: `stop:${norm(name)}`,
          });
        } else {
          // no served coordinate → no pin. Published so the gate can see it, never guessed onto the map.
          unresolved.push(name);
        }
      }
      host.setAttribute("data-stops-unresolved", String(unresolved.length));
      if (unresolved.length) host.setAttribute("data-stops-unresolved-names", unresolved.join(" | "));
      root.render(<KeylessMap markers={markers} surface="profile-stops" />);
    };

    // `active-tab-button` is live's own selected-tab class; the swap MOVES it rather than inventing one.
    const select = (which: "pics" | "stops") => {
      const on = which === "stops";
      host.style.display = on ? "" : "none";
      media.style.display = on ? "none" : "";
      stopsBtn.classList.toggle("active-tab-button", on);
      picsBtn.classList.toggle("active-tab-button", !on);
      stopsBtn.setAttribute("aria-selected", String(on));
      picsBtn.setAttribute("aria-selected", String(!on));
    };

    const onStops = (e: Event) => {
      e.preventDefault();
      void renderMap();
      select("stops");
    };
    const onPics = (e: Event) => {
      e.preventDefault();
      select("pics");
    };

    // the whole point of the finding: the control stops being disabled
    stopsBtn.classList.remove("disabled");
    stopsBtn.removeAttribute("disabled");
    stopsBtn.style.cursor = "pointer";
    stopsBtn.setAttribute("data-behavior", "profile-stops-tab");
    picsBtn.setAttribute("data-behavior", "profile-pics-tab");
    stopsBtn.addEventListener("click", onStops);
    picsBtn.addEventListener("click", onPics);

    return () => {
      alive = false;
      stopsBtn.removeEventListener("click", onStops);
      picsBtn.removeEventListener("click", onPics);
      media.style.display = "";
      queueMicrotask(() => root.unmount());
      host.remove();
    };
  }, [payload]);

  return null;
}
